// Why: the hover popover, rung 3 of the disclosure ladder (DETAIL_LEVELS.md),
// and Term, its one-sentence cousin for a label nobody should have to guess.
//
// It says where one number came from, so a manager who does not trust our read
// on a player can see what moved it and discount it. That is what makes "it is
// on them for how much they trust the predictions" a real offer rather than a
// shrug (docs/decisions/2026-09-12-tools-not-instructions.md).
//
// Rules it keeps, from the ladder:
//   - Read-only and transient. No buttons, no links, no scrolling. Anything
//     needing those is a detail sheet, not a popover.
//   - Available at EVERY detail level. The level controls the face of a row;
//     it never decides whether a number can explain itself.
//   - Touch parity: hover on a pointer, a tap (or a long press) on touch.
//     It was a long press alone, which nobody finds: a number's dotted
//     underline says "ask me", and a tap is how a phone asks (the UX pass of
//     2026-10-11, UX_STANDARDS UX-1). A click with a mouse pins it open; a
//     keyboard opens it on focus. The tap is the number's own, never the row's
//     under it (UX-2).
//   - HELP WAITS TO BE ASKED (B-374). A mouse resting on it, or the keyboard
//     resting on it, opens it after `HELP_DELAY_MS`, as a desktop's own
//     tooltips do; a pointer passing over a table opens nothing. Once one
//     has shown, its neighbors answer at once for a moment, so a reader going
//     along a row of heads is not made to wait at each. A click, a tap, a
//     long press and Enter are asking outright, and answer at once. The
//     pointer can move onto what opened without it closing (WCAG 1.4.13).
//
// TERM says what a word or an abbreviation means — PF, L3, "3rd fewest", a
// plan's colors — where a native `title` did, which a phone never shows and a
// mouse shows only after a wait with no sign it was there (UX_STANDARDS UX-1,
// UX-25; the page-by-page review of 2026-10-11). Same gestures, same rules:
// words only, transient, at every level.

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Popover } from '@mantine/core';

const LONG_PRESS_MS = 400;

/**
 * How long a mouse or the keyboard rests on a word or a number before it
 * explains itself (B-374): long enough that a pointer crossing a table opens
 * nothing, short enough that someone who wants it is not kept waiting. The
 * kit's `Hint` on an icon button waits the same.
 */
export const HELP_DELAY_MS = 900;
/** Once one has shown: how soon a neighbor answers, and for how long after it closes. */
const WARM_DELAY_MS = 120;
const WARM_FOR_MS = 700;
/** How long a hover's explanation stays after the pointer leaves, to be reached. */
const LEAVE_GRACE_MS = 120;

/**
 * The warm moment, anywhere on the page: while a hover's explanation is open,
 * and for a moment after the last one closed, the next one answers soon. A
 * pointer sliding from one head to the next arrives while the first is still
 * in its leave's grace, so an open one counts (the review of 2026-10-17).
 */
let hoversOpen = 0;
let lastHoverClose = 0;

export interface WhyRow {
  label: string;
  value: string;
  /** A quiet qualifier beside the value: "avg", "of MIN", "of 32". */
  note?: string;
}

/**
 * The gestures both share: a mouse's hover, a tap or a long press, a click
 * that pins, the keyboard's focus and Enter, and Escape. Spread `target` on
 * the element that carries the affordance.
 */
function useExplain(onActivate?: () => void) {
  const [opened, setOpen] = useState(false);
  // THE TRUTH OF OPEN, read by timers and handlers: the state is what was
  // last drawn, and a pointer can leave between a rest's timer opening it
  // and the draw (the review of 2026-10-17).
  const openRef = useRef(false);
  // Whether a hover opened it: it counts toward the page's warm moment.
  const byHover = useRef(false);
  // A rest's wait to open, and a leave's grace to close.
  const timer = useRef<number | null>(null);
  const press = useRef<number | null>(null);
  // How it was opened: a hover closes when the pointer leaves; anything
  // asked outright stays until a click outside, Escape or a blur.
  const how = useRef<'hover' | 'asked'>('asked');
  // A mouse's click keeps it open when the pointer leaves; a click outside,
  // Escape or a blur closes it and lets go (the review of 2026-10-11).
  const pinned = useRef(false);
  const setOpened = useCallback((next: boolean | ((was: boolean) => boolean)) => {
    const value = typeof next === 'function' ? next(openRef.current) : next;
    if (value === openRef.current) return;
    openRef.current = value;
    if (!value) {
      if (byHover.current) {
        byHover.current = false;
        hoversOpen -= 1;
        lastHoverClose = Date.now();
      }
      pinned.current = false;
    }
    setOpen(value);
  }, []);
  // How the last press began, and whether a long press already answered it,
  // so the click that follows a press is read the way the press meant it.
  const pointer = useRef<string>('mouse');
  const pressed = useRef(false);

  const clearTimer = useCallback(() => {
    if (timer.current !== null) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);
  const clearPress = useCallback(() => {
    if (press.current !== null) {
      clearTimeout(press.current);
      press.current = null;
    }
  }, []);

  useEffect(
    () => () => {
      clearTimer();
      clearPress();
      // Gone while a hover held it open: the page's count lets go of it.
      if (byHover.current) {
        byHover.current = false;
        hoversOpen -= 1;
        lastHoverClose = Date.now();
      }
    },
    [clearTimer, clearPress],
  );

  // Escape closes it, like every other transient surface in the app.
  useEffect(() => {
    if (!opened) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpened(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [opened, setOpened]);

  /** A rest: open after the wait, or soon beside one that just showed. */
  const rest = (warmable: boolean) => {
    clearTimer();
    const warm = warmable && (hoversOpen > 0 || Date.now() - lastHoverClose < WARM_FOR_MS);
    timer.current = window.setTimeout(
      () => {
        timer.current = null;
        if (openRef.current) return;
        how.current = 'hover';
        byHover.current = true;
        hoversOpen += 1;
        setOpened(true);
      },
      warm ? WARM_DELAY_MS : HELP_DELAY_MS,
    );
  };
  /** A leave: a hover's explanation closes after a grace the pointer can cross in. */
  const leave = () => {
    clearTimer();
    if (!openRef.current || pinned.current || how.current !== 'hover') return;
    timer.current = window.setTimeout(() => {
      timer.current = null;
      setOpened(false);
    }, LEAVE_GRACE_MS);
  };
  /** Asked outright: a click, a tap, a long press, Enter. */
  const ask = (next: boolean | ((was: boolean) => boolean)) => {
    clearTimer();
    // Asked for while a hover held it open: it is pinned now, not a hover,
    // and no longer keeps the page's neighbors quick (the review of 2026-10-17).
    if (byHover.current) {
      byHover.current = false;
      hoversOpen -= 1;
    }
    how.current = 'asked';
    setOpened(next);
  };

  const target = {
    tabIndex: 0,
    role: 'button' as const,
    'aria-expanded': opened,
    // A press that acts says so to the stylesheet: a head's sort, a cell's
    // door. It looks like what it does, not like a word to be asked about.
    'data-acts': onActivate !== undefined ? '' : undefined,
    // A mouse's hover, and only a mouse's: a tap raises a hover of its own
    // before its click, which opened it for the click to shut.
    onPointerEnter: (e: React.PointerEvent) => {
      if (e.pointerType !== 'mouse') return;
      if (openRef.current) clearTimer();
      else rest(true);
    },
    // The keyboard's focus rests too; a tap's focus does not, or the tap's
    // own click would close it again at once.
    onFocus: (e: React.FocusEvent<HTMLElement>) => {
      if (e.currentTarget.matches(':focus-visible') && !openRef.current) rest(false);
    },
    onBlur: () => {
      clearTimer();
      setOpened(false);
    },
    onClick: (e: React.MouseEvent) => {
      // The number's own tap, never the row's under it (UX-2).
      e.stopPropagation();
      if (pressed.current) {
        pressed.current = false;
        return;
      }
      // A PRESS THAT ACTS (a column head's sort, B-366): the tap does the
      // thing, and the meaning stays a rest, a long press or a focus away.
      if (onActivate !== undefined) {
        clearTimer();
        setOpened(false);
        onActivate();
        return;
      }
      if (pointer.current === 'mouse') {
        ask(true);
        pinned.current = true;
      } else ask((o) => !o);
    },
    onKeyDown: (e: React.KeyboardEvent) => {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      e.preventDefault();
      e.stopPropagation();
      if (onActivate !== undefined) onActivate();
      else ask((o) => !o);
    },
    onPointerDown: (e: React.PointerEvent) => {
      pointer.current = e.pointerType;
      pressed.current = false;
      if (e.pointerType === 'mouse') return;
      clearPress();
      press.current = window.setTimeout(() => {
        press.current = null;
        pressed.current = true;
        ask(true);
      }, LONG_PRESS_MS);
    },
    onPointerUp: clearPress,
    onPointerCancel: clearPress,
    onPointerLeave: (e: React.PointerEvent) => {
      clearPress();
      if (e.pointerType === 'mouse') leave();
    },
    onContextMenu: (e: React.MouseEvent) => {
      // A long press raises the context menu on some touch browsers; the
      // popover is the answer we want, not "copy link".
      if (openRef.current) e.preventDefault();
    },
  };
  // What opened is part of the target for a pointer: resting on it keeps it.
  // A click or a tap on it closes it, and goes no further: drawn in a portal,
  // its events still reach the row the word sits in, which would open the
  // row's own sheet under it (the review of 2026-10-17).
  const dropdown = {
    onPointerEnter: (e: React.PointerEvent) => {
      if (e.pointerType === 'mouse') clearTimer();
    },
    onPointerLeave: (e: React.PointerEvent) => {
      if (e.pointerType === 'mouse') leave();
    },
    onClick: (e: React.MouseEvent) => {
      e.stopPropagation();
      clearTimer();
      setOpened(false);
    },
  };
  return { opened, setOpened, target, dropdown };
}

export interface WhyProps {
  /** What the number belongs to: a player, a team. */
  subject: string;
  /** The number being explained, as shown. */
  value: string;
  /** What moved it. Facts only — never a conclusion about who to play. */
  rows: WhyRow[];
  /** One quiet line under the rows, e.g. naming this as a projection. */
  footnote?: string;
  /** The line under the subject; "Why <value>" by default. */
  caption?: string;
  /** The number's accessible name; "Why <subject> is <value>" by default. */
  label?: string;
  /** The number itself. Gets the affordance styling and the handlers. */
  children: ReactNode;
}

export function Why({ subject, value, rows, footnote, caption, label, children }: WhyProps) {
  const { opened, setOpened, target, dropdown } = useExplain();
  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      position="top"
      withArrow
      shadow="md"
      width={260}
      trapFocus={false}
      returnFocus={false}
      withinPortal
    >
      <Popover.Target>
        <span className="fsp-why" aria-label={label ?? `Why ${subject} is ${value}`} {...target}>
          {children}
        </span>
      </Popover.Target>
      <Popover.Dropdown className="fsp-why-pop" {...dropdown}>
        <div className="fsp-why-head">
          <div>
            <div className="fsp-why-subject">{subject}</div>
            <div className="fsp-cap">{caption ?? `Why ${value}`}</div>
          </div>
        </div>
        <div className="fsp-why-rows">
          {rows.map((r) => (
            <div className="fsp-why-row" key={r.label}>
              <span className="fsp-why-label">{r.label}</span>
              <span className="fsp-why-val">
                {r.note ? <span className="fsp-why-note">{r.note}</span> : null}
                <span className="fsp-num">{r.value}</span>
              </span>
            </div>
          ))}
        </div>
        {footnote ? <div className="fsp-why-foot">{footnote}</div> : null}
      </Popover.Dropdown>
    </Popover>
  );
}

export interface TermProps {
  /** What it means, in a sentence or two. Words only. */
  tip: string;
  /**
   * Its accessible name's first words, which are the visible ones (WCAG
   * 2.5.3); the children when they are a string.
   */
  name?: string;
  className?: string;
  /** The word, abbreviation or chip being explained. */
  children: ReactNode;
  /**
   * What a press does, when it does something rather than explain: a column
   * head's sort (B-366). A tap, a click or Enter calls it; the meaning opens
   * on a mouse's hover, a long press and the keyboard's focus, as before.
   */
  onActivate?: () => void;
  /** Its state, said after the meaning: "Sorted, highest first." */
  state?: string;
}

export function Term({ tip, name, className, children, onActivate, state }: TermProps) {
  const { opened, setOpened, target, dropdown } = useExplain(onActivate);
  const words = name ?? (typeof children === 'string' ? children : '');
  const said = `${words ? `${words}: ${tip}` : tip}${state ? ` ${state}` : ''}`;
  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      position="top"
      withArrow
      shadow="md"
      trapFocus={false}
      returnFocus={false}
      withinPortal
    >
      <Popover.Target>
        <span className={`fsp-term ${className ?? ''}`} aria-label={said} {...target}>
          {children}
        </span>
      </Popover.Target>
      <Popover.Dropdown className="fsp-why-pop fsp-term-pop" maw={280} {...dropdown}>
        {tip}
      </Popover.Dropdown>
    </Popover>
  );
}
